'use client';

/**
 * Recently Viewed Products - Client Utilities
 *
 * Client-side utilities for managing the recently viewed cookie.
 * The cookie is read server-side by the orchestrator.
 */

/**
 * Cookie name for recently viewed products (must match server.ts)
 */
const RECENTLY_VIEWED_COOKIE = 'breitling_recently_viewed';

/**
 * Maximum number of recently viewed products to track
 */
const MAX_RECENTLY_VIEWED = 10;

/**
 * Cookie max age: 30 days in seconds
 */
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60;

/**
 * Parse the recently viewed cookie value
 */
function parseRecentlyViewedCookie(): string[] {
  if (typeof document === 'undefined') return [];

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === RECENTLY_VIEWED_COOKIE && value) {
      return decodeURIComponent(value).split(',').filter(Boolean);
    }
  }
  return [];
}

/**
 * Set the recently viewed cookie
 */
function setRecentlyViewedCookie(skus: string[]): void {
  if (typeof document === 'undefined') return;

  const value = skus.join(',');
  const expires = new Date(Date.now() + COOKIE_MAX_AGE * 1000).toUTCString();

  // Set cookie with path=/ so it's available across the site
  document.cookie = `${RECENTLY_VIEWED_COOKIE}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax`;
}

/**
 * Add a SKU to the recently viewed list
 * Called when a product page is viewed
 *
 * @param sku - Product SKU to add
 */
export function addToRecentlyViewed(sku: string): void {
  if (!sku) return;

  const existing = parseRecentlyViewedCookie();

  // Remove if already exists (will be re-added at end)
  const filtered = existing.filter((s) => s !== sku);

  // Add to end (most recent)
  filtered.push(sku);

  // Limit to max items (remove oldest from beginning)
  const limited = filtered.slice(-MAX_RECENTLY_VIEWED);

  setRecentlyViewedCookie(limited);
}

/**
 * Clear all recently viewed products
 */
export function clearRecentlyViewed(): void {
  if (typeof document === 'undefined') return;

  // Set expired cookie to delete
  document.cookie = `${RECENTLY_VIEWED_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Get recently viewed SKUs (client-side)
 * Note: For SSR, use the server.ts version
 */
export function getRecentlyViewedSkusClient(): string[] {
  return parseRecentlyViewedCookie();
}
